import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AcceptEnum } from '@commons/file-picker/models/accept.enum';
import { FILE_PICKER_UPLOAD_SERVICE_TOKEN } from '@commons/file-picker/services/file-picker-upload-service.token';
import { FileWebServiceInterface } from '@commons/file-picker/services/file.web-service.interface';
import { WINDOW } from '@wizbii/angular-utilities';
import { BehaviorSubject, Subscription } from 'rxjs';

enum UploadStep {
  Waiting,
  Uploading,
  ShowFile,
}

@Component({
  selector: 'app-ui-file-picker',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MainComponent),
      multi: true,
    },
  ],
})
export class MainComponent implements ControlValueAccessor, OnInit {
  private static Count = 0;

  @Input() accept!: AcceptEnum[];
  @Input() maxSize = 8; // taille en Mo
  @Input() caption?: string;
  @HostBinding('id') id: string;
  progress!: number | undefined;
  progressSubscription!: Subscription | undefined;

  model: any;
  disabled!: boolean;
  onTouched!: () => void;
  onChange!: (file: any) => void;

  UploadStep = UploadStep;
  step$ = new BehaviorSubject<UploadStep>(UploadStep.Waiting);

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    @Inject(FILE_PICKER_UPLOAD_SERVICE_TOKEN) private readonly fileWebService: FileWebServiceInterface,
    @Inject(WINDOW) private readonly window: any
  ) {
    this.id = `file-picker__${++MainComponent.Count}`;
  }

  ngOnInit(): void {
    if (!this.maxSize) {
      throw new Error('`maxSize` in wzb-ui-file-picker must be set');
    }
  }

  @HostListener('document:click', ['$event'])
  doOutsideClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.onTouched();
    }
  }

  registerOnChange(onChange: (url: string) => void): void {
    this.onChange = (file: any) => {
      const callback = (url: string) => {
        if (this.progressSubscription) {
          this.progressSubscription.unsubscribe();
          this.progressSubscription = undefined;
          this.progress = undefined;
        }

        if (onChange) {
          onChange(url);
        }
      };

      if (file) {
        this.step$.next(UploadStep.Uploading);
        this.progressSubscription = this.fileWebService.post(file).subscribe({
          next: (next) =>
            this.window.requestAnimationFrame(() => {
              if (typeof next === 'number') {
                this.progress = next;
              } else {
                this.step$.next(UploadStep.ShowFile);
                callback(next);
              }

              this.cdr.detectChanges();
            }),
        });
      } else {
        this.step$.next(UploadStep.Waiting);
        callback('');
      }

      this.model = file;
      this.cdr.detectChanges();
    };
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.detectChanges();
  }

  async writeValue(url: string): Promise<void> {
    this.model = url ? await this.fileWebService.get(url).toPromise() : undefined;
    this.step$.next(this.model ? UploadStep.ShowFile : UploadStep.Waiting);
    this.cdr.detectChanges();
  }
}
